import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { useLocation as useLocationContext } from '../../../contexts/LocationContext';
import { grafikService } from '../../../services/grafikService';
import { locationsService } from '../../../services/locationsService';
import {
  formatHours, formatMonthYear, getMonthsList, toISODate
} from '../../../utils/grafikHelpers';
import EmployeeList from '../grafik/EmployeeList';
import MonthCalendar from '../grafik/MonthCalendar';
import ShiftEditModal from '../grafik/ShiftEditModal';
import CopyWeekModal from '../grafik/CopyWeekModal';
import BulkAssignModal from '../grafik/BulkAssignModal';
import LocationHoursModal from '../grafik/LocationHoursModal';
import LiveStatusPanel from '../grafik/LiveStatusPanel';
import PlanVsRealView from '../grafik/PlanVsRealView';
import PunktualnoscRaport from '../grafik/PunktualnoscRaport';
import AllEmployeesMatrix from '../grafik/AllEmployeesMatrix';

/**
 * Admin: Zakladka Grafik Pracy.
 * Lewa kolumna - lista pracownikow + suma godzin.
 * Prawa kolumna - kalendarz miesiaca dla wybranego pracownika.
 */
const GrafikTab = () => {
  const { locationId: ctxLocationId } = useLocationContext();

  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedLocationId, setSelectedLocationId] = useState(ctxLocationId || '');
  const [locations, setLocations] = useState([]);
  const [viewMode, setViewMode] = useState('grafik'); // 'grafik' | 'plan-vs-real' | 'punktualnosc'
  const [viewLayout, setViewLayout] = useState('single'); // 'single' (hybryda) | 'all' (macierz wszystkich)
  const [allShifts, setAllShifts] = useState([]);
  const [allShiftsLoading, setAllShiftsLoading] = useState(false);
  const [allAvailability, setAllAvailability] = useState({}); // {userLogin: {iso: [entries]}}

  const [usersList, setUsersList] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  const [selectedUserLogin, setSelectedUserLogin] = useState(null);
  const [userShifts, setUserShifts] = useState([]);
  const [shiftsLoading, setShiftsLoading] = useState(false);

  const [summary, setSummary] = useState({});

  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showLocationHoursModal, setShowLocationHoursModal] = useState(false);
  const [editingShift, setEditingShift] = useState(null);
  const [addingShiftDate, setAddingShiftDate] = useState(null);

  // Pobierz lokalizacje
  useEffect(() => {
    locationsService.getLocations().then(res => {
      const arr = res?.data?.locations || res?.locations || res?.data || [];
      setLocations(Array.isArray(arr) ? arr : []);
    }).catch(err => console.error('[GrafikTab] Blad ladowania lokalizacji:', err));
  }, []);

  // Sync z LocationContext
  useEffect(() => {
    if (ctxLocationId && !selectedLocationId) {
      setSelectedLocationId(ctxLocationId);
    }
  }, [ctxLocationId, selectedLocationId]);

  // Pobierz liste pracownikow
  const loadUsersList = useCallback(async () => {
    // Backend /grafik/users-list wymaga location_id (w przeciwienstwie do
    // /grafik/month i /grafik/summary, ktore toleruja brak lokalizacji).
    // Bez wybranej lokalizacji ('— wszystkie —') nie wolamy endpointu, zeby
    // nie dostac 400 i nie wywalac panelu pustym error-toastem.
    if (!selectedLocationId) {
      setUsersList([]);
      setSelectedUserLogin(null);
      setUsersLoading(false);
      return;
    }
    setUsersLoading(true);
    try {
      const res = await grafikService.getUsersList({
        locationId: selectedLocationId,
        year: selectedYear,
        month: selectedMonth
      });
      const arr = res?.data?.users || res?.data || res?.users || [];
      setUsersList(Array.isArray(arr) ? arr : []);
      // Auto-select pierwszego usera jezeli zadnego nie wybrano
      if (Array.isArray(arr) && arr.length > 0) {
        setSelectedUserLogin(prev => (prev && arr.some(u => u.login === prev)) ? prev : arr[0].login);
      } else {
        setSelectedUserLogin(null);
      }
    } catch (err) {
      toast.error(err.message || 'Blad ladowania pracownikow');
    } finally {
      setUsersLoading(false);
    }
  }, [selectedLocationId, selectedYear, selectedMonth]);

  // Pobierz podsumowanie
  const loadSummary = useCallback(async () => {
    try {
      const res = await grafikService.getSummary({
        year: selectedYear,
        month: selectedMonth,
        locationId: selectedLocationId || undefined
      });
      const data = res?.data || res;
      // Zakladamy ze backend zwraca {[login]: {...}} lub array
      let map = {};
      if (Array.isArray(data?.summary)) {
        data.summary.forEach(s => { if (s.user_login) map[s.user_login] = s; });
      } else if (data?.summary && typeof data.summary === 'object') {
        map = data.summary;
      } else if (Array.isArray(data)) {
        data.forEach(s => { if (s.user_login) map[s.user_login] = s; });
      } else if (data && typeof data === 'object') {
        map = data;
      }
      setSummary(map);
    } catch (err) {
      console.warn('[GrafikTab] Blad summary:', err.message);
    }
  }, [selectedYear, selectedMonth, selectedLocationId]);

  // Pobierz zmiany wybranego pracownika
  const loadUserShifts = useCallback(async () => {
    if (!selectedUserLogin) {
      setUserShifts([]);
      return;
    }
    setShiftsLoading(true);
    try {
      const res = await grafikService.getUserShifts(selectedUserLogin, {
        year: selectedYear,
        month: selectedMonth,
        locationId: selectedLocationId || undefined
      });
      const arr = res?.data?.shifts || res?.data || res?.shifts || [];
      setUserShifts(Array.isArray(arr) ? arr : []);
    } catch (err) {
      toast.error(err.message || 'Blad ladowania zmian');
    } finally {
      setShiftsLoading(false);
    }
  }, [selectedUserLogin, selectedYear, selectedMonth, selectedLocationId]);

  useEffect(() => { loadUsersList(); }, [loadUsersList]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadUserShifts(); }, [loadUserShifts]);

  // Wszystkie zmiany w miesiacu - dla widoku macierzowego ORAZ
  // dla widoku pojedynczego (zeby pokazac wspolpracownikow w kalendarzu).
  const loadAllShifts = useCallback(async () => {
    if (viewMode !== 'grafik') return;
    setAllShiftsLoading(true);
    try {
      const res = await grafikService.getMonth({
        year: selectedYear, month: selectedMonth,
        locationId: selectedLocationId || undefined
      });
      const arr = res?.data?.shifts || res?.shifts || res?.data || [];
      setAllShifts(Array.isArray(arr) ? arr : []);
    } catch (err) {
      toast.error(err.message || 'Blad ladowania grafiku miesiecznego');
    } finally {
      setAllShiftsLoading(false);
    }
  }, [viewMode, selectedYear, selectedMonth, selectedLocationId]);

  useEffect(() => { loadAllShifts(); }, [loadAllShifts]);

  // Dostepnosc dla wszystkich pracownikow w widoku macierzowym (parallel fetch)
  const loadAllAvailability = useCallback(async () => {
    if (viewLayout !== 'all' || viewMode !== 'grafik') return;
    if (!usersList || usersList.length === 0) {
      setAllAvailability({});
      return;
    }
    try {
      const results = await Promise.all(
        usersList.map(u =>
          grafikService.getUserAvailability(u.login, selectedYear, selectedMonth)
            .then(res => ({ login: u.login, data: res?.data || res }))
            .catch(() => ({ login: u.login, data: null }))
        )
      );
      const map = {};
      results.forEach(r => {
        if (!r.data) return;
        const entries = r.data.entries || r.data || [];
        const byDate = {};
        (Array.isArray(entries) ? entries : []).forEach(e => {
          const iso = String(e.data || '').slice(0, 10);
          if (!iso) return;
          if (!byDate[iso]) byDate[iso] = [];
          byDate[iso].push(e);
        });
        map[r.login] = byDate;
      });
      setAllAvailability(map);
    } catch (err) {
      console.warn('[GrafikTab] Blad ladowania dostepnosci wszystkich:', err.message);
    }
  }, [viewLayout, viewMode, selectedYear, selectedMonth, usersList]);

  useEffect(() => { loadAllAvailability(); }, [loadAllAvailability]);

  const goPrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(y => y - 1);
    } else {
      setSelectedMonth(m => m - 1);
    }
  };
  const goNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(y => y + 1);
    } else {
      setSelectedMonth(m => m + 1);
    }
  };

  const onShiftClick = (shift) => {
    setEditingShift(shift);
    setAddingShiftDate(null);
  };

  const onEmptyDayClick = (date) => {
    setEditingShift(null);
    setAddingShiftDate(date);
  };

  const handleModalSaved = () => {
    loadUserShifts();
    loadSummary();
    loadUsersList();
    loadAllShifts();
  };

  const handleExportCsv = async () => {
    try {
      const blob = await grafikService.exportCsv({
        year: selectedYear, month: selectedMonth,
        locationId: selectedLocationId || undefined
      });
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      const locSuffix = selectedLocationId ? `_loc${selectedLocationId}` : '_all';
      a.download = `grafik_${selectedYear}_${String(selectedMonth).padStart(2, '0')}${locSuffix}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV pobrany');
    } catch (err) {
      toast.error(err.message || 'Blad eksportu CSV');
    }
  };

  const handleExportPdf = async () => {
    try {
      const blob = await grafikService.exportPdf({
        year: selectedYear, month: selectedMonth,
        locationId: selectedLocationId || undefined
      });
      const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      const locSuffix = selectedLocationId ? `_loc${selectedLocationId}` : '_all';
      a.download = `grafik_${selectedYear}_${String(selectedMonth).padStart(2, '0')}${locSuffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('PDF pobrany');
    } catch (err) {
      toast.error(err.message || 'Blad eksportu PDF');
    }
  };

  const yearsRange = useMemo(() => {
    const cur = today.getFullYear();
    return [cur - 1, cur, cur + 1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalUserHours = useMemo(() => {
    return userShifts.reduce((sum, s) => {
      const h = Number(s.godziny_planowane || s.hours || 0);
      return sum + (isNaN(h) ? 0 : h);
    }, 0);
  }, [userShifts]);

  const initialDataForCreate = useMemo(() => ({
    user_login: selectedUserLogin || '',
    location_id: selectedLocationId || '',
    data: addingShiftDate ? toISODate(addingShiftDate) : toISODate(new Date()),
    status: 'planowana',
    godzina_od: '08:00',
    godzina_do: '16:00',
    przerwa_minut: 0
  }), [selectedUserLogin, selectedLocationId, addingShiftDate]);

  const btnPrimary = {
    padding: '6px 12px',
    backgroundColor: 'var(--info)', color: '#fff',
    border: 'none', cursor: 'pointer',
    fontSize: '12px', fontWeight: 500
  };
  const btnSecondary = {
    padding: '6px 12px',
    backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)',
    border: '1px solid var(--border)', cursor: 'pointer',
    fontSize: '12px', fontWeight: 500
  };

  const tabBtn = (active) => ({
    padding: '6px 12px',
    backgroundColor: active ? 'var(--info)' : 'var(--card-bg)',
    color: active ? '#fff' : 'var(--text-secondary)',
    border: '1px solid ' + (active ? 'var(--info)' : 'var(--border)'),
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: active ? 600 : 500
  });

  return (
    <div>
      {/* Pasek przełączania widoków (Etap 6+7) */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.4rem',
        flexWrap: 'wrap',
        marginBottom: '0.75rem'
      }}>
        <button onClick={() => setViewMode('grafik')} style={tabBtn(viewMode === 'grafik')}>
          <i className="fas fa-calendar" style={{ marginRight: '0.4rem' }} />
          Grafik
        </button>
        <button onClick={() => setViewMode('plan-vs-real')} style={tabBtn(viewMode === 'plan-vs-real')}>
          <i className="fas fa-balance-scale" style={{ marginRight: '0.4rem' }} />
          Plan vs Realizacja
        </button>
        <button onClick={() => setViewMode('punktualnosc')} style={tabBtn(viewMode === 'punktualnosc')}>
          <i className="fas fa-stopwatch" style={{ marginRight: '0.4rem' }} />
          Punktualnosc
        </button>
      </div>

      {/* Live status panel - widoczny zawsze nad zawartoscia */}
      <LiveStatusPanel locationId={selectedLocationId || undefined} />

      {viewMode === 'plan-vs-real' && (
        <PlanVsRealView
          year={selectedYear}
          month={selectedMonth}
          locationId={selectedLocationId}
          onYearChange={setSelectedYear}
          onMonthChange={setSelectedMonth}
          onLocationChange={setSelectedLocationId}
          locations={locations}
        />
      )}

      {viewMode === 'punktualnosc' && (
        <PunktualnoscRaport
          year={selectedYear}
          month={selectedMonth}
          locationId={selectedLocationId}
          onYearChange={setSelectedYear}
          onMonthChange={setSelectedMonth}
          onLocationChange={setSelectedLocationId}
          locations={locations}
          users={usersList}
        />
      )}

      {viewMode === 'grafik' && (
      <>
      {/* Pasek gorny */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem',
        padding: '0.75rem 1rem', backgroundColor: 'var(--bg)',
        border: '1px solid var(--border)', borderRadius: '6px',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <button onClick={goPrevMonth} style={btnSecondary} title="Poprzedni miesiac">&laquo;</button>
          <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}
            style={{
              padding: '6px 10px',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)',
              border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px'
            }}>
            {getMonthsList().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}
            style={{
              padding: '6px 10px',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)',
              border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px'
            }}>
            {yearsRange.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button onClick={goNextMonth} style={btnSecondary} title="Nastepny miesiac">&raquo;</button>
        </div>

        <div style={{
          fontSize: '0.95rem', fontWeight: 600, color: 'var(--text)',
          marginLeft: '0.5rem', marginRight: '0.5rem'
        }}>
          {formatMonthYear(selectedYear, selectedMonth)}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Lokalizacja:</span>
          <select
            value={selectedLocationId}
            onChange={(e) => setSelectedLocationId(e.target.value)}
            style={{
              padding: '6px 10px',
              backgroundColor: 'var(--card-bg)', color: 'var(--text)',
              border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px'
            }}
          >
            <option value="">— wszystkie —</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.nazwa || l.name}</option>)}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginLeft: '0.5rem' }}>
          <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginRight: '0.25rem' }}>Widok:</span>
          <button
            onClick={() => setViewLayout('single')}
            style={{
              padding: '6px 12px', fontSize: '12px',
              border: '1px solid var(--border)',
              borderRadius: '6px 0 0 6px',
              borderRight: 'none',
              backgroundColor: viewLayout === 'single' ? 'var(--info)' : 'var(--card-bg)',
              color: viewLayout === 'single' ? '#fff' : 'var(--text-secondary)',
              fontWeight: viewLayout === 'single' ? 600 : 500,
              cursor: 'pointer'
            }}
            title="Hybryda - lista pracownikow + kalendarz wybranego"
          >
            <i className="fas fa-user" style={{ marginRight: '0.3rem' }} />
            Pojedynczy
          </button>
          <button
            onClick={() => setViewLayout('all')}
            style={{
              padding: '6px 12px', fontSize: '12px',
              border: '1px solid var(--border)',
              borderRadius: '0 6px 6px 0',
              backgroundColor: viewLayout === 'all' ? 'var(--info)' : 'var(--card-bg)',
              color: viewLayout === 'all' ? '#fff' : 'var(--text-secondary)',
              fontWeight: viewLayout === 'all' ? 600 : 500,
              cursor: 'pointer'
            }}
            title="Macierz - wszyscy pracownicy x dni miesiaca"
          >
            <i className="fas fa-th" style={{ marginRight: '0.3rem' }} />
            Wszyscy
          </button>
        </div>

        <div style={{ flex: 1 }} />

        <button
          onClick={() => setShowLocationHoursModal(true)}
          style={btnSecondary}
          disabled={!selectedLocationId}
          title={!selectedLocationId ? 'Wybierz lokalizacje' : 'Domyslne godziny pracy lokalizacji'}
        >
          <i className="fas fa-clock" style={{ marginRight: '0.35rem' }} />
          Godziny pracy
        </button>
        <button onClick={() => setShowCopyModal(true)} style={btnSecondary}>
          <i className="fas fa-copy" style={{ marginRight: '0.35rem' }} />
          Kopiuj tydzien
        </button>
        <button onClick={() => setShowBulkAssignModal(true)} style={btnSecondary}>
          <i className="fas fa-users-cog" style={{ marginRight: '0.35rem' }} />
          Bulk dodaj
        </button>
        <button onClick={handleExportCsv} style={btnSecondary}>
          <i className="fas fa-file-csv" style={{ marginRight: '0.35rem' }} />
          Eksport CSV
        </button>
        <button onClick={handleExportPdf} style={btnSecondary}>
          <i className="fas fa-file-pdf" style={{ marginRight: '0.35rem' }} />
          Eksport PDF
        </button>
        <button
          onClick={() => { setEditingShift(null); setAddingShiftDate(new Date()); }}
          style={btnPrimary}
          disabled={!selectedUserLogin && !selectedLocationId}
        >
          <i className="fas fa-plus" style={{ marginRight: '0.35rem' }} />
          Dodaj zmiane
        </button>
      </div>

      {/* Widok: single (hybryda) lub all (macierz) */}
      {viewLayout === 'single' && (
        <div style={{
          display: 'flex', gap: '1rem', flexWrap: 'wrap',
          border: '1px solid var(--border)', borderRadius: '6px',
          overflow: 'hidden', backgroundColor: 'var(--card-bg)'
        }}>
          <EmployeeList
            users={usersList}
            summary={summary}
            selectedUserLogin={selectedUserLogin}
            onSelect={setSelectedUserLogin}
            loading={usersLoading}
          />

          <div style={{ flex: 1, padding: '1rem', minWidth: 0 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '0.75rem'
            }}>
              <div>
                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
                  {selectedUserLogin
                    ? <>Kalendarz: <span style={{ color: 'var(--info)' }}>{selectedUserLogin}</span></>
                    : 'Kalendarz miesiaca'}
                </h4>
                {selectedUserLogin && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>
                    Suma godzin (z zaladowanych zmian): <strong>{formatHours(totalUserHours)}</strong>
                  </div>
                )}
              </div>
            </div>

            {!selectedUserLogin && (
              <div style={{
                padding: '2rem', textAlign: 'center', color: 'var(--text-muted)',
                backgroundColor: 'var(--bg)', borderRadius: '4px', fontSize: '0.9rem'
              }}>
                {!selectedLocationId
                  ? 'Wybierz lokalizacje aby zaladowac liste pracownikow.'
                  : 'Wybierz pracownika z listy po lewej stronie aby wyswietlic jego grafik.'}
              </div>
            )}

            {selectedUserLogin && shiftsLoading && (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                Ladowanie zmian...
              </div>
            )}

            {selectedUserLogin && !shiftsLoading && (
              <div className="admin-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <MonthCalendar
                  year={selectedYear}
                  month={selectedMonth}
                  shifts={userShifts}
                  coworkerShifts={allShifts}
                  selectedUserLogin={selectedUserLogin}
                  onShiftClick={onShiftClick}
                  onEmptyDayClick={onEmptyDayClick}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {viewLayout === 'all' && (
        <div style={{
          padding: '1rem',
          border: '1px solid var(--border)', borderRadius: '6px',
          backgroundColor: 'var(--card-bg)'
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: '0.75rem'
          }}>
            <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text)' }}>
              Macierz: wszyscy pracownicy x dni miesiaca
            </h4>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              Klik komorki: pusty = dodaj, ze zmiana = edytuj
            </div>
          </div>

          {allShiftsLoading && (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              Ladowanie grafiku miesiecznego...
            </div>
          )}

          {!allShiftsLoading && (
            <div className="admin-table-wrap" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <AllEmployeesMatrix
                year={selectedYear}
                month={selectedMonth}
                users={usersList}
                shifts={allShifts}
                availability={allAvailability}
                onEditShift={(s) => onShiftClick(s)}
                onAddShift={({ user_login, date }) => {
                  setEditingShift(null);
                  setAddingShiftDate(new Date(date + 'T00:00:00'));
                  setSelectedUserLogin(user_login);
                }}
              />
            </div>
          )}
        </div>
      )}
      </>
      )}

      {/* Modale */}
      {showCopyModal && (
        <CopyWeekModal
          onClose={() => setShowCopyModal(false)}
          onCopied={() => { loadUserShifts(); loadSummary(); }}
        />
      )}

      {showBulkAssignModal && (
        <BulkAssignModal
          locations={locations}
          defaultLocationId={selectedLocationId || ''}
          onClose={() => setShowBulkAssignModal(false)}
          onAdded={handleModalSaved}
        />
      )}

      {(editingShift || addingShiftDate) && (
        <ShiftEditModal
          mode={editingShift ? 'edit' : 'create'}
          initialData={editingShift || initialDataForCreate}
          users={usersList}
          locations={locations}
          onClose={() => { setEditingShift(null); setAddingShiftDate(null); }}
          onSaved={handleModalSaved}
        />
      )}

      {showLocationHoursModal && selectedLocationId && (
        <LocationHoursModal
          locationId={Number(selectedLocationId)}
          locationName={locations.find(l => String(l.id) === String(selectedLocationId))?.nazwa}
          onClose={() => setShowLocationHoursModal(false)}
        />
      )}
    </div>
  );
};

export default GrafikTab;
